import type { MaintenanceEvent } from '~/api/types'
import { ActiveAnalysisMessages } from './active-analysis-messages'
import { AnalysisProgress } from './analysis-progress'
import { ClarificationMessages } from './clarification-messages'
import { Message } from './message'
import type {
    MaintenanceProgress,
    PendingClarification,
    ResultItem,
} from './types'
import { formatDateTime } from './utils'

type AnalysisMessagesProps = {
    activeEvent: MaintenanceEvent | null
    history: MaintenanceEvent[] | null
    submittedText: string | null
    pendingClarification: PendingClarification | null
    progress: MaintenanceProgress | null
    resultItems: ResultItem[]
    checklistCount: number
    onSelectImpact: (itemId: string) => void
    onCopyRecheckItems: () => void
    onOpenReport: (opts?: { print?: boolean }) => void
}

function ProgressMessage({
    progress,
    changeDescription,
}: {
    progress: MaintenanceProgress
    changeDescription: string | null
}) {
    // 백엔드 분석은 단건 호출이라 실시간 진행 이벤트가 없다. 단계별 인디케이터·분석 대상
    // 미리보기·도메인 팁으로 수십 초 대기의 체감을 개선한다(타이머 기반 연출).
    return (
        <Message from='bot' wide>
            <AnalysisProgress
                progress={progress}
                changeDescription={changeDescription}
            />
        </Message>
    )
}

/** 유지하기 분석 워크스페이스의 대화 메시지 영역을 조립한다. */
export function AnalysisMessages({
    activeEvent,
    history,
    submittedText,
    pendingClarification,
    progress,
    resultItems,
    checklistCount,
    onSelectImpact,
    onCopyRecheckItems,
    onOpenReport,
}: AnalysisMessagesProps) {
    return (
        <div className='mx-auto grid max-w-4xl gap-4'>
            <div className='text-muted-foreground flex items-center justify-center text-xs'>
                <span className='bg-background border-border rounded-sm border px-3 py-1'>
                    {activeEvent
                        ? `${formatDateTime(activeEvent.createdAt)} 분석`
                        : history && history.length > 0
                          ? '변경 내용을 입력하거나 왼쪽 이력을 선택하세요'
                          : '새 변경 분석을 시작하세요'}
                </span>
            </div>

            <Message from='bot'>
                <p>
                    최근 바뀐 점이 있으면 편하게 적어 주세요. 인증에 어떤 영향이
                    있는지 같이 확인해 드릴게요.
                </p>
            </Message>

            {activeEvent ? (
                <ActiveAnalysisMessages
                    activeEvent={activeEvent}
                    resultItems={resultItems}
                    checklistCount={checklistCount}
                    onSelectImpact={onSelectImpact}
                    onCopyRecheckItems={onCopyRecheckItems}
                    onOpenReport={onOpenReport}
                />
            ) : pendingClarification ? (
                <ClarificationMessages
                    pendingClarification={pendingClarification}
                />
            ) : submittedText || progress ? null : (
                // 아직 아무것도 보내지 않은 초기 상태에서만 입력 예시를 안내한다.
                // 사용자가 변경을 보내면(submittedText/progress) 이 안내는 사라지고
                // 사용자 말풍선·진행 메시지가 시간순으로 자연스럽게 이어진다.
                <Message from='bot'>
                    <p>
                        예: “주방에 자동 진공포장기를 새로 들였어요”, “가열
                        기준을 75℃에서 80℃로 바꿨어요”처럼 입력하면 됩니다.
                    </p>
                </Message>
            )}

            {/* 낙관적 사용자 에코와 진행 메시지는 안내 다음, 즉 항상 맨 끝에 append 한다. */}
            {submittedText ? (
                <Message from='user'>
                    <p className='whitespace-pre-wrap'>{submittedText}</p>
                </Message>
            ) : null}

            {progress ? (
                <ProgressMessage
                    progress={progress}
                    changeDescription={submittedText}
                />
            ) : null}
        </div>
    )
}
